import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Minus, Plus } from "lucide-react";
import { message } from "antd";
import foodApiService, { EditFoodItemPayload } from "../../services/foodApiService";
import { buildEditFoodPayload } from "../../utils/foodEditPayload";

interface FoodItem {
  id: number;
  food_item_id?: string;
  name: string;
  calories: number;
  weight: number;
  servings: number;
  count?: number;
  predicted_count?: number;
  predicted_serving_size_g?: number;
  predicted_serving_description?: string;
  unit_label?: string;
  predicted_unit_label?: string;
  image: string;
  carbohydrate_content_g?: number;
  protein_content_g?: number;
  fat_content_g?: number;
  dietary_fiber_g?: number;
  net_carbs_g?: number;
  sugar_g?: number;
  trans_fat_g?: number;
  saturated_fat_g?: number;
  GI_estimate?: number;
  GL_estimate?: number;
  serving_description?: string;
}

const EditFoodPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const foodItem = location.state?.foodItem as FoodItem;

  const [editedData, setEditedData] = useState({
    name: "",
    servingDescription: "",
    servings: 0,
    weight: 0,
    calories: 0,
  });

  // Serving multiplier: initialized from foodItem.servings, 0.5 steps, range 0.5 - 20
  const [servingMultiplier, setServingMultiplier] = useState<number>(
    () => (foodItem?.servings && foodItem.servings > 0 ? foodItem.servings : 1)
  );

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (foodItem) {
      setEditedData({
        name: foodItem.name,
        servingDescription: foodItem.serving_description ?? "",
        servings: foodItem.servings,
        weight: foodItem.weight,
        calories: foodItem.calories,
      });
      // Initialize servingMultiplier: use servings from item, or parse from serving_description
      const parseFromDesc = (desc?: string) => {
        if (!desc) return 1;
        const match = desc.match(/^(\d+(?:\.\d+)?)\s/);
        const parsed = match ? parseFloat(match[1]) : 1;
        return parsed > 0 ? parsed : 1;
      };
      const initialServings =
        foodItem.servings && foodItem.servings > 0
          ? foodItem.servings
          : parseFromDesc(foodItem.serving_description);
      setServingMultiplier(initialServings);
    }
  }, [foodItem]);

  const handleDecrement = () => {
    setServingMultiplier(prev => Math.max(0.5, parseFloat((prev - 0.5).toFixed(1))));
  };

  const handleIncrement = () => {
    setServingMultiplier(prev => Math.min(20, parseFloat((prev + 0.5).toFixed(1))));
  };

  const handleSave = async () => {
    // Validate required fields
    if (!editedData.name.trim()) {
      message.error("Please enter food name");
      return;
    }

    setIsLoading(true);

    try {
      const apiPayload: EditFoodItemPayload = buildEditFoodPayload(
        foodItem,
        editedData.name,
        servingMultiplier
      );

      let responseData: any = null;

      if (foodItem.food_item_id) {
        const response = await foodApiService.editFoodItem(
          foodItem.food_item_id,
          apiPayload
        );
        responseData = (response.data as any)?.food_items?.[0] ?? response.data;
      } else {
        const response = await foodApiService.editManualFood(apiPayload);
        responseData = (response.data as any)?.item ?? (response.data as any)?.data ?? response.data;
      }

      message.success("Food item updated successfully!");

      const responseCount = responseData?.count ?? servingMultiplier;

      const updatedFoodItem = {
        ...foodItem,
        name: responseData?.name ?? editedData.name.trim(),
        serving_description: responseData?.serving_description ?? foodItem.serving_description,
        count: responseCount,
        servings: responseCount,
        weight: responseData?.serving_size_g ?? foodItem.weight,
        calories: responseData?.calorie_content_kcal ?? responseData?.calories ?? foodItem.calories,
        protein_content_g: responseData?.protein_content_g ?? foodItem.protein_content_g,
        carbohydrate_content_g: responseData?.carbohydrate_content_g ?? foodItem.carbohydrate_content_g,
        net_carbs_g: responseData?.net_carbs_g ?? foodItem.net_carbs_g,
        fat_content_g: responseData?.fat_content_g ?? foodItem.fat_content_g,
        trans_fat_g: responseData?.trans_fat_g ?? foodItem.trans_fat_g,
        saturated_fat_g: responseData?.saturated_fat_g ?? foodItem.saturated_fat_g,
        sugar_g: responseData?.sugar_g ?? foodItem.sugar_g,
        dietary_fiber_g: responseData?.dietary_fiber_g ?? foodItem.dietary_fiber_g,
        GI_estimate: responseData?.GI_estimate ?? foodItem.GI_estimate,
        GL_estimate: responseData?.GL_estimate ?? foodItem.GL_estimate,
      };

      // Store edit in sessionStorage so ScannedFooddetailPage can reliably read it
      // regardless of React effect execution order (race condition prevention)
      sessionStorage.setItem("glucera_pending_food_edit", JSON.stringify(updatedFoodItem));

      navigate("/glucera/scanned-food-detail", {
        state: {
          localUpdatedItem: updatedFoodItem,
          fromEditPage: true,
        },
      });
    } catch (error: any) {
      message.error(error.message || "Failed to update food item");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (!foodItem) {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[400px] items-center justify-center bg-background">
        <div className="text-foreground">No food item data found</div>
      </div>
    );
  }

  return (
    <div className="mx-auto min-h-screen w-full max-w-[400px] bg-white">
      <div className="space-y-6 p-4">
        {/* Header */}
        <div className="mb-6 flex items-center">
          <button
            onClick={handleBack}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="ml-4 text-lg font-semibold text-gray-900">
            Edit Food Item
          </h1>
        </div>

        {/* Food Name */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Food Name</label>
          <input
            type="text"
            value={editedData.name}
            onChange={e =>
              setEditedData({
                ...editedData,
                name: e.target.value,
              })
            }
            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter food name"
            maxLength={100}
          />
        </div>

        {/* Serving Size */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Serving Size</label>
          {/* Labels row */}
          <div className="flex gap-3">
            <div className="w-[130px]">
              <span className="text-xs text-gray-500">Qty</span>
            </div>
            <div className="flex-1">
              <span className="text-xs text-gray-500">Unit</span>
            </div>
          </div>
          {/* Boxes row — same height via items-stretch */}
          <div className="flex items-stretch gap-3">
            <div className="flex w-[130px] items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3">
              <button
                type="button"
                onClick={handleDecrement}
                disabled={servingMultiplier <= 0.5}
                className="flex h-7 w-7 items-center justify-center rounded-md bg-white shadow-sm transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Minus className="h-4 w-4 text-gray-600" />
              </button>
              <span className="w-8 text-center text-base font-semibold text-gray-900">
                {servingMultiplier}
              </span>
              <button
                type="button"
                onClick={handleIncrement}
                disabled={servingMultiplier >= 20}
                className="flex h-7 w-7 items-center justify-center rounded-md bg-white shadow-sm transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Plus className="h-4 w-4 text-gray-600" />
              </button>
            </div>
            <div className="flex flex-1 items-center rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
              {(editedData.servingDescription || foodItem.serving_description || `${foodItem.weight}g`).replace(/^\d+(\.\d+)?\s*/, "")}
            </div>
          </div>
          <p className="text-xs text-gray-400">
            Adjust servings (0.5 to 20) · portion description is auto-detected
          </p>
        </div>

        {/* Current Nutrition Info Card */}
        <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h3 className="mb-3 text-sm font-medium text-gray-800">
            Current Nutrition Info
          </h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Calories:</span>
              <span className="font-medium text-gray-900">
                {foodItem.calories} kcal
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Protein:</span>
              <span className="font-medium text-gray-900">
                {Math.round(foodItem.protein_content_g || 0)}g
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Carbs:</span>
              <span className="font-medium text-gray-900">
                {Math.round(foodItem.carbohydrate_content_g || 0)}g
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Net Carbs:</span>
              <span className="font-medium text-orange-500">
                {Math.round(foodItem.net_carbs_g || 0)}g
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Fiber:</span>
              <span className="font-medium text-orange-400">
                {Math.round(foodItem.dietary_fiber_g || 0)}g
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Fat:</span>
              <span className="font-medium text-gray-900">
                {Math.round(foodItem.fat_content_g || 0)}g
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Saturated Fat:</span>
              <span className="font-medium text-gray-900">
                {Math.round(foodItem.saturated_fat_g || 0)}g
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Sugar:</span>
              <span className="font-medium text-orange-400">
                {Math.round(foodItem.sugar_g || 0)}g
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">GI:</span>
              <span className="font-medium text-gray-900">
                {Math.round(foodItem.GI_estimate || 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">GL:</span>
              <span className="font-medium text-purple-500">
                {Math.round(foodItem.GL_estimate || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 pt-4">
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="w-full rounded-lg bg-gradient-primary py-3 font-medium text-primary-foreground shadow-primary transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? "Saving Changes..." : "Save Changes"}
          </button>

          <button
            onClick={handleBack}
            className="w-full rounded-lg border border-gray-300 bg-white py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditFoodPage;
